import { createFileRoute, Link } from '@tanstack/react-router';
import { tokenStore } from '@/lib/auth';
import { PublicHeader } from '@/components/public/public-header';
import { PublicFooter } from '@/components/public/public-footer';
import { useRouteMeta } from '@/lib/use-route-meta';

export const Route = createFileRoute('/')({ component: HomePage });

const TESTIMONIALS = [
  {
    name: 'Clara Navarro',
    initials: 'CN',
    text: 'Comecei há pouco, mas é nítido como minha psicóloga me entende. Me sinto ouvida e acolhida em cada sessão.',
    gradient: 'from-teal-200 to-teal-400',
  },
  {
    name: 'Patricia Ramos',
    initials: 'PR',
    text: 'A Nosso Ello aproxima cliente e profissional com cuidado. Consigo agendar com facilidade e sem burocracia.',
    gradient: 'from-coral-200 to-coral-400',
  },
];

function HomePage() {
  const isLoggedIn = !!tokenStore.getAccess();
  useRouteMeta({
    title: 'Encontre terapeutas e psicólogos online — cuidado emocional',
    description: 'Nosso Ello conecta você a terapeutas e psicólogos verificados. Agende sessões online via Google Meet ou presenciais, pague com segurança e receba acolhimento sob medida.',
    canonical: 'https://nossoello.com.br/',
  });

  return (
    <div className="min-h-screen bg-[#FBF8F3]">
      <PublicHeader />

      {/* ─── HERO ─── */}
      <section className="relative">
        <div className="max-w-6xl mx-auto px-6 pt-16 pb-32 grid md:grid-cols-2 gap-12 items-center relative z-10">
          <div>
            <h1 className="text-5xl md:text-6xl font-extrabold text-slate-700 leading-[1.1] tracking-tight">
              Conecte-se com profissionais de forma acolhedora
            </h1>
            <p className="text-xl text-slate-400 mt-6">Cuidado emocional que aproxima</p>
            {isLoggedIn ? (
              <Link
                to="/dashboard"
                className="inline-flex mt-10 bg-teal-500 hover:bg-teal-600 text-white px-10 py-4 rounded-full font-medium text-lg shadow-lg shadow-teal-200 transition"
              >
                Ir para o painel →
              </Link>
            ) : (
              <Link
                to="/register"
                className="inline-flex mt-10 bg-coral-400 hover:bg-coral-500 text-white px-10 py-4 rounded-full font-medium text-lg shadow-lg shadow-coral-200 transition"
              >
                Começar agora
              </Link>
            )}
          </div>

          <div className="relative">
            <div
              className="relative w-full aspect-[5/4] overflow-hidden shadow-2xl shadow-slate-300/40"
              style={{ borderRadius: '48px 48px 96px 48px' }}
            >
              <img
                src="/hero.png"
                alt="Casal acolhido em atendimento psicológico"
                className="object-cover w-full h-full"
              />
            </div>
          </div>
        </div>

        <HeroDepthShadow />
      </section>

      {/* ─── FEATURE CARDS ─── */}
      <section id="solucao" className="max-w-6xl mx-auto px-6 -mt-4 pb-20 grid md:grid-cols-3 gap-5">
        <FeatureCard
          tone="teal"
          icon={<ShieldIcon />}
          title="Atendimento seguro e acessível"
          desc="Sessões online com privacidade e confiança."
        />
        <FeatureCard
          tone="coral"
          icon={<HeartsIcon />}
          title="Profissionais qualificados"
          desc="Encontre profissionais experientes e empáticos."
        />
        <FeatureCard
          tone="cream"
          icon={<EmbraceIcon />}
          title="Conexão humana"
          desc="Construa vínculos reais com profissionais."
        />
      </section>

      {/* ─── TESTIMONIALS ─── */}
      <section className="max-w-6xl mx-auto px-6 py-12 grid md:grid-cols-2 gap-5">
        {TESTIMONIALS.map((t) => (
          <article
            key={t.name}
            className="bg-white/80 backdrop-blur rounded-3xl p-6 flex gap-4 items-start shadow-sm border border-slate-100/60 relative"
          >
            <div
              className={`size-14 rounded-full shrink-0 bg-gradient-to-br ${t.gradient} flex items-center justify-center text-white font-bold`}
            >
              {t.initials}
            </div>
            <div className="flex-1">
              <h4 className="font-bold text-slate-700">{t.name}</h4>
              <p className="text-slate-500 text-sm mt-1.5 line-clamp-2">{t.text}</p>
            </div>
          </article>
        ))}
      </section>

      {/* ─── BOTTOM CTA BANNER ─── */}
      <section className="max-w-6xl mx-auto px-6 py-8">
        <div className="bg-gradient-to-r from-coral-100 via-coral-200 to-coral-300 rounded-full px-8 py-5 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-slate-800 font-semibold text-lg text-center md:text-left">
            Comece sua jornada de cuidado emocional hoje mesmo
          </p>
          {isLoggedIn ? (
            <Link
              to="/dashboard"
              className="bg-teal-600 hover:bg-teal-700 text-white px-7 py-3 rounded-full font-medium whitespace-nowrap shadow transition"
            >
              Ir para o painel →
            </Link>
          ) : (
            <Link
              to="/register"
              className="bg-coral-500 hover:bg-coral-600 text-white px-7 py-3 rounded-full font-medium whitespace-nowrap shadow transition"
            >
              Começar agora
            </Link>
          )}
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}

/* ─── COMPONENTS ─────────────────────────────────────────────────────────── */

function FeatureCard({
  tone, icon, title, desc,
}: {
  tone: 'teal' | 'coral' | 'cream';
  icon: React.ReactNode;
  title: string;
  desc: string;
}) {
  const styles = {
    teal: { bg: 'bg-teal-300', text: 'text-white', subtext: 'text-white/90', iconBg: 'bg-white/20' },
    coral: { bg: 'bg-coral-300', text: 'text-white', subtext: 'text-white/90', iconBg: 'bg-white/20' },
    cream: { bg: 'bg-coral-100', text: 'text-slate-700', subtext: 'text-slate-600', iconBg: 'bg-white' },
  }[tone];

  return (
    <div className={`${styles.bg} p-7 rounded-[28px] shadow-sm`}>
      <div className={`size-12 rounded-2xl ${styles.iconBg} flex items-center justify-center mb-4`}>
        {icon}
      </div>
      <h3 className={`text-lg font-bold ${styles.text} mb-2`}>{title}</h3>
      <p className={`text-sm ${styles.subtext} leading-relaxed`}>{desc}</p>
    </div>
  );
}

function HeroDepthShadow() {
  return (
    <div aria-hidden className="absolute inset-x-0 bottom-0 pointer-events-none">
      <div
        className="absolute inset-x-0 bottom-[-40%] h-[420px]"
        style={{
          background:
            'radial-gradient(ellipse 60% 55% at 50% 50%, rgba(240,144,128,0.20) 0%, rgba(112,208,192,0.10) 45%, transparent 75%)',
          filter: 'blur(40px)',
        }}
      />
      <svg viewBox="0 0 1440 180" className="absolute inset-x-0 bottom-0 w-full h-[160px]" preserveAspectRatio="none">
        <defs>
          <linearGradient id="waveBack" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="#70D0C0" stopOpacity="0.18" />
            <stop offset="50%" stopColor="#F09080" stopOpacity="0.14" />
            <stop offset="100%" stopColor="#70D0C0" stopOpacity="0.18" />
          </linearGradient>
        </defs>
        <path d="M0,120 C240,40 480,180 720,100 C960,20 1200,140 1440,80 L1440,180 L0,180 Z" fill="url(#waveBack)" />
      </svg>
      <svg viewBox="0 0 1440 140" className="absolute inset-x-0 bottom-[-20px] w-full h-[140px]" preserveAspectRatio="none">
        <defs>
          <linearGradient id="waveFront" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="#F09080" stopOpacity="0.10" />
            <stop offset="50%" stopColor="#F09080" stopOpacity="0.22" />
            <stop offset="100%" stopColor="#F09080" stopOpacity="0.10" />
          </linearGradient>
        </defs>
        <path d="M0,80 C320,10 640,130 1000,60 C1200,30 1320,90 1440,60 L1440,140 L0,140 Z" fill="url(#waveFront)" />
      </svg>
    </div>
  );
}

function ShieldIcon() {
  return (
    <svg className="size-6 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}

function HeartsIcon() {
  return (
    <svg className="size-6 text-white" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 21s-7-4.5-9.5-9.5C.8 8 2.7 4 6.5 4c2 0 3.5 1 4.5 2.5C12 5 13.5 4 15.5 4 19.3 4 21.2 8 19.5 11.5 17 16.5 12 21 12 21Z" />
    </svg>
  );
}

function EmbraceIcon() {
  return (
    <svg className="size-6 text-coral-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="8" r="3" />
      <circle cx="15" cy="8" r="3" />
      <path d="M3 20c1-4 4-6 9-6s8 2 9 6" />
    </svg>
  );
}

